import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, FileText, Pencil, Play, Square, WifiOff } from 'lucide-react';
import type { MeterFrame, PairRef, ProbeInfo, StreamSummary, Target } from '@shared/protocol';
import { api } from '../lib/api';
import { useDocumentTitle } from '../lib/branding';
import { LOUDNESS_TARGETS, targetFromPreset, type LoudnessTarget } from '../lib/meterMath';
import { useStreamLive } from '../lib/useStreamLive';
import { useTargets } from '../lib/useTargets';
import { Goniometer } from '../components/meters/Goniometer';
import { LevelMeters } from '../components/meters/LevelMeters';
import { LoudnessHistory } from '../components/meters/LoudnessHistory';
import { LoudnessPanel, groupOf } from '../components/meters/LoudnessPanel';
import { SpectrumPanel } from '../components/meters/SpectrumPanel';
import { StereoPanel } from '../components/meters/StereoPanel';
import { FindingsList } from '../components/FindingsList';
import { Player, type PlayerSource } from '../components/Player';
import { StreamInfo } from '../components/StreamInfo';
import { Notice, StreamStatusBadge } from '../components/Status';
import { SummaryPanel } from '../components/SummaryPanel';
import { TransportPanel } from '../components/TransportPanel';
import { VideoPanel } from '../components/VideoPanel';

const typeLabel = { hls: 'HLS', youtube: 'YouTube Live', srt: 'SRT' } as const;

/** Stereo pairs the goniometer can show: each group's front pair and each track's consecutive pairs. */
function pairOptions(frame: MeterFrame | null): { ref: PairRef; label: string }[] {
  const out: { ref: PairRef; label: string }[] = [];
  const seen = new Set<string>();
  const add = (ref: PairRef, label: string) => {
    const k = `${ref.track}:${ref.a}:${ref.b}`;
    if (!seen.has(k)) {
      seen.add(k);
      out.push({ ref, label });
    }
  };
  for (const g of frame?.groups ?? []) if (g.channels.length >= 2) add({ track: g.track, a: g.channels[0], b: g.channels[1] }, g.name);
  for (const t of frame?.tracks ?? []) {
    for (let c = 0; c + 1 < t.channels; c += 2) add({ track: t.track, a: c, b: c + 1 }, `${frame!.tracks.length > 1 ? `Track ${t.track + 1} ` : ''}${t.labels[c]}/${t.labels[c + 1]}`);
  }
  return out;
}

function playerSource(stream: StreamSummary | null, videoId: string | null): PlayerSource {
  if (!stream) return { kind: 'none', reason: 'Loading' };
  if (stream.type === 'hls') return { kind: 'hls', url: stream.previewUrl ?? '' };
  if (stream.type === 'srt') return stream.monitoring ? { kind: 'hls', url: stream.previewUrl ?? '', serverPreview: true } : { kind: 'none', reason: 'Start monitoring to see the SRT preview.' };
  const id = videoId ?? stream.sourceLabel.match(/(?:youtu\.be\/|[?&]v=|\/live\/)([A-Za-z0-9_-]{6,20})/)?.[1] ?? null;
  return id ? { kind: 'youtube', videoId: id } : { kind: 'none', reason: 'The YouTube player appears once the stream has been resolved.' };
}

export function MonitorPage() {
  const id = Number(useParams().id);
  const [stream, setStream] = useState<StreamSummary | null>(null);
  const [initialProbe, setInitialProbe] = useState<ProbeInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [groupId, setGroupId] = useState<string | null>(null);
  const [pair, setPair] = useState<PairRef>({ track: 0, a: 0, b: 1 });
  const targets = useTargets();
  const { live, status, probe, frame, findings, video, transport, transportSeries, connected } = useStreamLive(id, pair);
  useDocumentTitle(stream?.name ?? 'Monitor');

  useEffect(() => {
    api<{ stream: StreamSummary; probe: ProbeInfo | null }>('GET', `/api/streams/${id}`)
      .then((r) => {
        setStream(r.stream);
        setInitialProbe(r.probe);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load the stream.'));
  }, [id]);

  const selectedTargets: Target[] = useMemo(() => (stream?.config.targetIds ?? []).map((t) => targets.find((x) => x.id === t)).filter((t): t is Target => !!t), [stream, targets]);

  const group = groupOf(frame, groupId);
  const groupConfig = stream?.config.groups.find((g) => g.id === group?.id);
  // Loudness target: the group's preset, else the first selected preset/profile with a loudness target.
  const target: LoudnessTarget = useMemo(() => {
    const own = groupConfig?.presetId ? targets.find((t) => t.id === groupConfig.presetId) : null;
    const pick = own ?? (group?.main ? selectedTargets.find((t) => t.spec.loudness) : selectedTargets.find((t) => t.kind === 'broadcaster' && t.spec.loudness)) ?? selectedTargets.find((t) => t.spec.loudness);
    return (pick && targetFromPreset(pick)) ?? LOUDNESS_TARGETS[0];
  }, [groupConfig, group?.main, selectedTargets, targets]);
  const mainTarget = useMemo(() => {
    const pick = selectedTargets.find((t) => t.spec.loudness);
    return (pick && targetFromPreset(pick)) ?? LOUDNESS_TARGETS[0];
  }, [selectedTargets]);

  const pairs = pairOptions(frame);
  const pairKey = `${pair.track}:${pair.a}:${pair.b}`;
  const pairLabel = pairs.find((p) => `${p.ref.track}:${p.ref.a}:${p.ref.b}` === pairKey)?.label ?? 'L/R';

  const toggle = async () => {
    if (!stream) return;
    setError(null);
    try {
      const r = await api<{ stream: StreamSummary }>('POST', `/api/streams/${id}/${stream.monitoring ? 'stop' : 'start'}`);
      setStream(r.stream);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The action failed.');
    }
  };

  const state = status ?? stream?.status ?? null;
  const running = state?.status === 'live' || state?.status === 'stalled';
  const yt = transport?.source?.youtube;

  if (error && !stream) {
    return (
      <div className="page">
        <Notice level="fail">{error}</Notice>
      </div>
    );
  }

  return (
    <div className="page monitor">
      <div className="page-header">
        <div>
          <Link to="/" className="back-link">
            <ArrowLeft size={14} aria-hidden /> Streams
          </Link>
          <div className="title-row">
            <h1>{stream?.name ?? 'Loading'}</h1>
            <StreamStatusBadge state={state} />
            {stream && <span className="badge badge-neutral">{typeLabel[stream.type]}</span>}
          </div>
          <p className="muted small mono truncate">{stream?.sourceLabel}</p>
          {yt && (
            <p className="muted small">
              {yt.channel}: {yt.title}
              {yt.height ? ` (monitoring the ${yt.height}p rendition)` : ''}
            </p>
          )}
          {stream?.listenAddress && (
            <p className="small">
              Encoder sends to <span className="mono">{stream.listenAddress}</span>
              {stream.config.srt?.streamId ? (
                <>
                  {' '}
                  with stream ID <span className="mono">{stream.config.srt.streamId}</span>
                </>
              ) : null}
              {stream.config.srt?.hasPassphrase ? ' (encrypted)' : ''}
            </p>
          )}
        </div>
        <div className="header-actions">
          {stream && (
            <>
              <Link className="btn" to={`/streams/${id}/reports`}>
                <FileText size={14} aria-hidden /> Reports
              </Link>
              <Link className="btn" to={`/streams/${id}/edit`}>
                <Pencil size={14} aria-hidden /> Edit
              </Link>
              <button type="button" className="btn btn-primary" onClick={() => void toggle()}>
                {stream.monitoring ? <Square size={14} aria-hidden /> : <Play size={14} aria-hidden />}
                {stream.monitoring ? 'Stop monitoring' : 'Start monitoring'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="monitor-notices">
        {!connected && (
          <Notice level="warn">
            <WifiOff size={14} aria-hidden style={{ verticalAlign: '-2px' }} /> Live connection to the monitor lost. Reconnecting. Monitoring continues on the server.
          </Notice>
        )}
        {state?.message && state.status !== 'live' && <Notice level={state.status === 'error' ? 'fail' : state.status === 'connecting' ? 'info' : 'warn'}>{state.message}</Notice>}
        {state?.message && state.status === 'live' && <Notice level="info">{state.message}</Notice>}
        {stream && !stream.monitoring && <Notice level="info">Monitoring is off for this stream. Start monitoring to see live measurements.</Notice>}
        {error && <Notice level="fail">{error}</Notice>}
      </div>

      {frame && frame.groups.length > 1 && (
        <div className="group-tabs" role="tablist" aria-label="Channel group">
          {frame.groups.map((g) => (
            <button
              key={g.id}
              type="button"
              role="tab"
              aria-selected={group?.id === g.id}
              className={group?.id === g.id ? 'active' : ''}
              onClick={() => {
                setGroupId(g.id);
                if (g.channels.length >= 2) setPair({ track: g.track, a: g.channels[0], b: g.channels[1] });
              }}
            >
              {g.name}
              {g.main ? <span className="muted small"> (main)</span> : null}
            </button>
          ))}
        </div>
      )}

      <div className="monitor-grid">
        <div className="area-summary">
          <SummaryPanel summary={findings?.summary ?? stream?.compliance ?? null} hasTargets={selectedTargets.length > 0} />
        </div>
        <div className="area-loudness">
          <LoudnessPanel
            live={live}
            frame={frame}
            groupId={group?.id ?? null}
            target={target}
            canReset={running}
            onReset={() => void api('POST', `/api/streams/${id}/reset-loudness`).catch((e) => setError(e.message))}
          />
        </div>
        <div className="area-levels">
          <LevelMeters live={live} frame={frame} maxTruePeak={target.maxTruePeak} />
        </div>
        <div className="area-gonio">
          <Goniometer
            live={live}
            pairLabel={pairLabel}
            pairControl={
              pairs.length > 1 ? (
                <select
                  className="input input-inline"
                  aria-label="Stereo pair"
                  value={pairKey}
                  onChange={(e) => {
                    const [track, a, b] = e.target.value.split(':').map(Number);
                    setPair({ track, a, b });
                  }}
                >
                  {pairs.map((p) => (
                    <option key={`${p.ref.track}:${p.ref.a}:${p.ref.b}`} value={`${p.ref.track}:${p.ref.a}:${p.ref.b}`}>
                      {p.label}
                    </option>
                  ))}
                </select>
              ) : null
            }
          />
        </div>
        <div className="area-history">
          <LoudnessHistory streamId={id} live={live} target={mainTarget} />
        </div>
        <div className="area-stereo">
          <StereoPanel live={live} frame={frame} groupId={group?.id ?? null} />
        </div>
        <div className="area-findings">
          <FindingsList findings={findings?.findings ?? []} targets={targets} />
        </div>
        <div className="area-player">
          <Player source={playerSource(stream, yt?.videoId ?? null)} />
        </div>
        <div className="area-spectrum">
          <SpectrumPanel live={live} groupId={group?.id ?? null} groupName={frame && frame.groups.length > 1 ? (group?.name ?? null) : null} />
        </div>
        <div className="area-transport">
          <TransportPanel stream={stream} transport={transport} series={transportSeries} />
        </div>
        <div className="area-video">
          <VideoPanel video={video} probe={probe ?? initialProbe} analysisEnabled={stream?.config.videoAnalysis ?? true} encoderVisible={stream?.type !== 'youtube'} />
        </div>
        <div className="area-info">
          <StreamInfo probe={probe ?? initialProbe} state={state} />
        </div>
      </div>
    </div>
  );
}
